<template>
    <v-dialog v-model="dialogSwitch" max-width="350px" persistent scrollable>
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy" @submit.prevent="generateInvoices">
            <v-card>
                <v-card-title class="d-flex justify-space-between align-center">
                    <div class="text-medium-emphasis ps-2">Generate Invoice</div>
                    <v-btn :disabled="loading" icon="mdi-close" size="small"
                           variant="text"
                           @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col class="pa-0" cols="12">
                            <v-radio-group v-model="formData['type']"
                                           label="Invoice for:"
                                           @update:model-value="resetValues">
                                <v-radio label="Single Company" value="1"/>
                                <v-radio label="Multiple Companies" value="2"/>
                            </v-radio-group>
                        </v-col>
                        <template v-if="formData['type']">
                            <v-col v-if="formData['type']==='2'" class="pa-0" cols="12">
                                <v-checkbox v-model="allCompanies"
                                            density="compact"
                                            hide-details
                                            label="All Companies"
                                            @update:model-value="onAllCompaniesToggle"/>
                            </v-col>
                            <v-col v-if="!allCompanies" class="pa-0" cols="12">
                                <company-select v-model="formData['company']"
                                                :items="companies"
                                                :multiple="formData['type']==='2'"
                                                :rules="[vRules.required]"
                                                chips
                                                closable-chips
                                                density="compact"
                                                :label="formData['type']==='2' ? 'Select Companies' : 'Select Company'"
                                                @update:model-value="fetchDevices"/>
                            </v-col>
                            <v-col v-if="allCompanies" class="pa-0 ps-2" cols="12">
                                <span class="text-caption text-medium-emphasis">
                                    <v-icon icon="mdi-check-circle" size="14" color="success" class="me-1"/>
                                    All {{ companies.length }} companies selected
                                </span>
                            </v-col>
                            <template v-if="formData['type']==1">
                                <v-col class="pa-0" cols="12">
                                    <v-checkbox v-model="allDevices"
                                                density="compact"
                                                hide-details
                                                label="All Devices"
                                                @update:model-value="val => { if (!val) fetchDevices(); else formData.devices = null; }"/>
                                </v-col>
                                <v-col v-if="!allDevices" class="pa-0" cols="12">
                                    <v-autocomplete v-model="formData['devices']"
                                                    :items="devices"
                                                    :loading="devicesLoading"
                                                    :rules="[vRules.required]"
                                                    density="compact"
                                                    item-title="slno" item-value="id" label="Select Device(s)"
                                                    multiple/>
                                </v-col>
                                <v-col class="pa-0" cols="12">
                                    <v-text-field v-model="formData['days']" autofocus
                                                  :rules="[vRules.numeric, vRules.between(1,31)]"
                                                  density="compact" label="No. of Days"/>
                                </v-col>
                            </template>
                            <v-col class="pa-0" cols="12">
                                <v-select v-model="formData['month']" :items="months" :rules="[vRules.required]"
                                          density="compact" label="Month"/>
                            </v-col>
                            <v-col class="pa-0" cols="12">
                                <v-select v-model="formData['year']" :items="years" :rules="[vRules.required]"
                                          density="compact" label="Year"/>

                            </v-col>
                        </template>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn :disabled="!formData.valid" :loading="loading" color="primary" size="small"
                           type="submit">Submit
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CompanySelect from "@/components/globals/company-select.vue";

const {showSnackbar} = useSnackbarStore();
const loading = ref(false);
const props = defineProps({
    modelValue: Boolean,
    companies: Array,
});
const dialogSwitch = ref(props.modelValue);
const emits = defineEmits(['update:modelValue', 'invoiceCreated']);

const formData = reactive({
    type: null,
    days: null,
    year: null,
    month: null,
    company: null,
    valid: false,
});
const months = [
    {value: 1, title: 'January'},
    {value: 2, title: 'February'},
    {value: 3, title: 'March'},
    {value: 4, title: 'April'},
    {value: 5, title: 'May'},
    {value: 6, title: 'June'},
    {value: 7, title: 'July'},
    {value: 8, title: 'August'},
    {value: 9, title: 'September'},
    {value: 10, title: 'October'},
    {value: 11, title: 'November'},
    {value: 12, title: 'December'},
];
const years = [];
for (let i = 2024; i <= (new Date()).getFullYear(); i++) {
    years.push(i.toString());
}
const devices = ref([])
const devicesLoading = ref(false)
const allCompanies = ref(false)
const allDevices = ref(true)

const resetValues = () => {
    formData['days'] = null;
    formData['year'] = null;
    formData['month'] = null;
    formData['company'] = null;
    formData['devices'] = null;
    formData['valid'] = false;
    allCompanies.value = false;
    allDevices.value = true;
    devices.value = [];
};
const onAllCompaniesToggle = (val) => {
    if (val) {
        formData['company'] = props.companies.map(c => c.id);
    } else {
        formData['company'] = [];
    }
};
const fetchDevices = () => {
    if (formData['type'] == 1 && !allDevices.value && formData['company']) {
        devicesLoading.value = true;
        send('get', `devices-list`, {company: formData['company']})
            .then((response) => {
                devices.value = response.data.data;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Devices could not be loaded', 'error')
            })
            .finally(() => devicesLoading.value = false);
    }
}
const generateInvoices = () => {
    if (!confirm("are you sure to generate invoices?")) {
        return false;
    }
    loading.value = true;
    const payload = {...formData};
    if (allDevices.value) delete payload.devices;
    send('post', `admin/invoices`, payload)
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                emits('invoiceCreated');
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.message) {
                showSnackbar(errorResponse.response.data.message, 'error');
            } else {
                showSnackbar('Something went wrong', 'error');
            }
        }).finally(() => loading.value = false);
}

</script>
